/**
 * AnimatedBackground - Joyful bouncing shapes for EchoDraw intro screen
 * Features circles, triangles, rectangles, lines, hexagons, ovals, and crosses
 * in yellow tones that harmonize with the blue gradient background
 */

import { motion } from 'framer-motion';

interface Shape {
  id: string;
  type: 'circle' | 'triangle' | 'rectangle' | 'line' | 'hexagon' | 'oval' | 'cross';
  x: number; // percentage
  y: number; // percentage
  size: number;
  color: string;
  duration: number;
}

// Generate random shapes with yellow color palette
const generateShapes = (): Shape[] => {
  const yellowColors = [
    '#FCD34D', // yellow-300
    '#FBBF24', // yellow-400
    '#F59E0B', // yellow-500
    '#FACC15', // yellow-400
    '#FDE047', // yellow-300
    '#EAB308', // yellow-500
  ];

  const shapeTypes: Shape['type'][] = [
    'circle', 'triangle', 'rectangle', 'line', 'hexagon', 'oval', 'cross'
  ];

  const shapes: Shape[] = [];

  // Generate 15-20 shapes randomly distributed
  const count = 15 + Math.floor(Math.random() * 6);

  for (let i = 0; i < count; i++) {
    shapes.push({
      id: `shape-${i}`,
      type: shapeTypes[Math.floor(Math.random() * shapeTypes.length)],
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 20 + Math.random() * 60,
      color: yellowColors[Math.floor(Math.random() * yellowColors.length)],
      duration: 3 + Math.random() * 4, // 3-7 seconds
    });
  }

  return shapes;
};

const shapes = generateShapes();

// Shape components
const Circle = ({ size, color }: { size: number; color: string }) => (
  <circle cx={size / 2} cy={size / 2} r={size / 2} fill={color} opacity={0.7} />
);

const Triangle = ({ size, color }: { size: number; color: string }) => {
  const points = `${size / 2},0 ${size},${size} 0,${size}`;
  return <polygon points={points} fill={color} opacity={0.7} />;
};

const Rectangle = ({ size, color }: { size: number; color: string }) => (
  <rect width={size} height={size * 0.6} fill={color} opacity={0.7} rx={4} />
);

const Line = ({ size, color }: { size: number; color: string }) => (
  <line
    x1={0}
    y1={size / 2}
    x2={size}
    y2={size / 2}
    stroke={color}
    strokeWidth={4}
    strokeLinecap="round"
    opacity={0.7}
  />
);

const Hexagon = ({ size, color }: { size: number; color: string }) => {
  const points = [
    [size * 0.5, 0],
    [size, size * 0.25],
    [size, size * 0.75],
    [size * 0.5, size],
    [0, size * 0.75],
    [0, size * 0.25],
  ].map(p => p.join(',')).join(' ');

  return <polygon points={points} fill={color} opacity={0.7} />;
};

const Oval = ({ size, color }: { size: number; color: string }) => (
  <ellipse
    cx={size / 2}
    cy={size / 2}
    rx={size / 2}
    ry={size / 3}
    fill={color}
    opacity={0.7}
  />
);

const Cross = ({ size, color }: { size: number; color: string }) => (
  <g opacity={0.7}>
    <line
      x1={size / 2}
      y1={0}
      x2={size / 2}
      y2={size}
      stroke={color}
      strokeWidth={4}
      strokeLinecap="round"
    />
    <line
      x1={0}
      y1={size / 2}
      x2={size}
      y2={size / 2}
      stroke={color}
      strokeWidth={4}
      strokeLinecap="round"
    />
    <line
      x1={size * 0.2}
      y1={size * 0.2}
      x2={size * 0.8}
      y2={size * 0.8}
      stroke={color}
      strokeWidth={4}
      strokeLinecap="round"
    />
    <line
      x1={size * 0.8}
      y1={size * 0.2}
      x2={size * 0.2}
      y2={size * 0.8}
      stroke={color}
      strokeWidth={4}
      strokeLinecap="round"
    />
  </g>
);

const ShapeRenderer = ({ type, size, color }: { type: Shape['type']; size: number; color: string }) => {
  switch (type) {
    case 'circle':
      return <Circle size={size} color={color} />;
    case 'triangle':
      return <Triangle size={size} color={color} />;
    case 'rectangle':
      return <Rectangle size={size} color={color} />;
    case 'line':
      return <Line size={size} color={color} />;
    case 'hexagon':
      return <Hexagon size={size} color={color} />;
    case 'oval':
      return <Oval size={size} color={color} />;
    case 'cross':
      return <Cross size={size} color={color} />;
  }
};

export function AnimatedBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {shapes.map((shape) => {
        const randomDelay = Math.random() * 3;
        const moveDistance = 40 + Math.random() * 60; // More movement

        return (
          <motion.div
            key={shape.id}
            className="absolute"
            style={{
              left: `${shape.x}%`,
              top: `${shape.y}%`,
              width: shape.size,
              height: shape.size,
            }}
            initial={{
              scale: 0,
              opacity: 0
            }}
            animate={{
              y: [0, -moveDistance, 0, moveDistance, 0],
              x: [0, moveDistance * 0.5, 0, -moveDistance * 0.5, 0],
              scale: [0, 1, 1.3, 1, 0],
              rotate: [0, 15, 0, -15, 0],
              opacity: [0, 1, 1, 1, 0],
            }}
            transition={{
              duration: shape.duration + 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: randomDelay,
              times: [0, 0.2, 0.5, 0.8, 1],
            }}
          >
            <svg
              width={shape.size}
              height={shape.size}
              viewBox={`0 0 ${shape.size} ${shape.size}`}
              className="drop-shadow-lg"
            >
              <ShapeRenderer type={shape.type} size={shape.size} color={shape.color} />
            </svg>
          </motion.div>
        );
      })}
    </div>
  );
}
